/**
 * Email Service for sending participant invitations
 * 
 * This service handles sending email invitations to participants.
 * It can be configured to use:
 * 1. Firebase Cloud Functions (recommended for production)
 * 2. Third-party email APIs (SendGrid, Mailgun, etc.)
 */

import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from './firebaseConfig';

const functions = getFunctions(app);

export interface InvitationEmailData {
    recipientEmail: string;
    projectName: string;
    projectId: string;
    inviterName: string;
    inviteLink: string;
}

/**
 * Send invitation email to a participant
 * This calls a Firebase Cloud Function that handles the actual email sending
 */
export const sendInvitationEmail = async (
    emailData: InvitationEmailData
): Promise<{ success: boolean; messageId?: string; error?: string }> => {
    try {
        // Call the Firebase Cloud Function
        const sendEmail = httpsCallable(functions, 'sendParticipantInvitation');

        const result = await sendEmail(emailData);

        return {
            success: true,
            messageId: (result.data as any)?.messageId,
        };
    } catch (error: any) {
        console.error('Error sending invitation email:', error);

        // If cloud function doesn't exist yet, log a helpful message
        if (error.code === 'functions/not-found') {
            console.warn(
                'Cloud function "sendParticipantInvitation" not found. ' +
                'Please deploy the cloud function or configure an alternative email service.'
            );
        }

        return {
            success: false,
            error: error.message || 'Failed to send invitation email',
        };
    }
};

/**
 * Send invitation emails to multiple participants
 */
export const sendBulkInvitationEmails = async (
    emails: string[],
    projectName: string,
    projectId: string,
    inviterName: string,
    baseInviteLink: string
): Promise<{ sent: number; failed: number; errors: string[] }> => {
    const results = {
        sent: 0,
        failed: 0,
        errors: [] as string[],
    };

    // Send emails in parallel
    const promises = emails.map(async (email) => {
        const emailData: InvitationEmailData = {
            recipientEmail: email,
            projectName,
            projectId,
            inviterName,
            inviteLink: baseInviteLink,
        };

        const result = await sendInvitationEmail(emailData);

        if (result.success) {
            results.sent++;
        } else {
            results.failed++;
            results.errors.push(`${email}: ${result.error}`);
        }
    });

    await Promise.all(promises);

    return results;
};

/**
 * Resend invitation email to a participant
 */
export const resendInvitationEmail = async (
    emailData: InvitationEmailData
): Promise<{ success: boolean; error?: string }> => {
    return sendInvitationEmail(emailData);
};
